import { useEffect } from "react";
import { Flex, InfoBox, Text } from "@jsq/ds/core";
import {
  FormCheckbox,
  FormDatePicker,
  FormRadioButton,
  FormRadioGroup,
  useFormContext,
} from "@jsq/ds/forms";
import { Colors } from "@jsq/ds/styles";
import { SPACING } from "~/styles/variables";
import { SYNC_SETTINGS_TYPES } from "./constants";
import { InitialValues } from "./types";

type Props = {
  glStartDate?: string;
  glAsOfDate?: string;
  revisedTransactionCount?: number;
  newTransactionCount?: number;
};

const CustomEndDateCard = ({
  glAsOfDate = "",
  glStartDate = "",
  revisedTransactionCount,
  newTransactionCount,
}: Props) => {
  const { values, setFieldValue } = useFormContext<InitialValues>();

  const noRevisedTransactions = revisedTransactionCount === 0;
  const noNewTransactions = newTransactionCount === 0;
  const noRevisedTransactionsTitle =
    "There are no transactions since the last synced GL date. Select a custom GL date to sync transactions.";
  const noTransactionsTitle =
    "No GL transactions have been created or updated since the last sync for the selected transaction categories";

  useEffect(() => {
    if (!noNewTransactions || !noRevisedTransactions) {
      setFieldValue("syncSettings", SYNC_SETTINGS_TYPES.sinceLastSync);
    }

    if (!noRevisedTransactions) {
      setFieldValue("updateDateRange", glAsOfDate);
    }

    if (noRevisedTransactions) {
      setFieldValue("updateDateRange", "custom");
    }
  }, [noNewTransactions, noRevisedTransactions, glAsOfDate, setFieldValue]);

  return (
    <>
      <>
        <FormRadioGroup name="syncSettings">
          <Text>What are you syncing?</Text>
          <Flex alignItems="center" css={{ paddingBottom: SPACING.SMALL }}>
            <FormRadioButton
              disabled={noNewTransactions && noRevisedTransactions}
              css={{
                color:
                  noNewTransactions && noRevisedTransactions ? Colors.Neutral600 : "inherit",
              }}
              value={SYNC_SETTINGS_TYPES.sinceLastSync}
            >
              GL transactions created or updated since the last sync
            </FormRadioButton>
          </Flex>
          <FormRadioButton
            css={{
              color: noNewTransactions && noRevisedTransactions ? Colors.Neutral600 : "inherit",
            }}
            value={SYNC_SETTINGS_TYPES.allTransactions}
          >
            <Flex flexDirection="column">
              <div>All GL transactions for a given GL date range</div>
              <div>
                <Text variant={"secondary-sm"}>
                  Only choose this option if you&apos;ve changed the transaction mapping and
                  need to re-sync all transactions
                </Text>
              </div>
            </Flex>
          </FormRadioButton>
        </FormRadioGroup>
      </>

      {values.syncSettings === SYNC_SETTINGS_TYPES.sinceLastSync && (
        <>
          <br></br>
          <FormRadioGroup name="updateDateRange" label="Through what date?">
            <Flex alignItems="center" css={{ paddingBottom: SPACING.SMALL }}>
              <FormRadioButton
                disabled={noRevisedTransactions}
                css={{
                  color: noRevisedTransactions ? Colors.Neutral600 : "inherit",
                }}
                value={glAsOfDate}
              >
                <Flex flexDirection="column">
                  <div>
                    <Text>Until last synced GL date</Text>
                  </div>
                  <div>
                    <Text variant={"secondary-sm"}>Until {glAsOfDate}</Text>
                  </div>
                </Flex>
              </FormRadioButton>
            </Flex>

            <FormRadioButton value="custom">
              <>Custom GL date</>
            </FormRadioButton>
          </FormRadioGroup>

          {values.updateDateRange === "custom" && (
            <Flex css={{ marginTop: SPACING.MEDIUM }} flexDirection="column">
              <Text variant="strong-md">
                <>Custom GL date</>
              </Text>
              <FormDatePicker displayErrorMessage name="customDate" hasIcon />
            </Flex>
          )}
        </>
      )}
      {values.syncSettings === SYNC_SETTINGS_TYPES.allTransactions && (
        <>
          <Flex
            css={{ marginTop: SPACING.MEDIUM }}
            flexDirection="column"
            alignItems="flex-start"
          >
            <Text variant="strong-md">Integration start</Text>
            <FormDatePicker
              displayErrorMessage
              name="integrationStart"
              hasIcon
              disabled={values.useInceptionDate}
              placeholderText={values.useInceptionDate ? glStartDate : ""}
            />
            <Flex css={{ marginTop: SPACING.SMALL }} flexDirection="column">
              <FormCheckbox
                name="useInceptionDate"
                label="Use inception date"
                disabled={false}
              />
            </Flex>
          </Flex>
          <Flex css={{ marginTop: SPACING.MEDIUM }} flexDirection="column">
            <Text variant="strong-md">Integration through</Text>
            <FormDatePicker displayErrorMessage name="integrationThrough" hasIcon />
          </Flex>
          <>
            <br></br>
            <InfoBox
              variant="info"
              title="Processing time may take a long time depending on your date range"
            >
              Larger date ranges and more transactions will take longer to preview and sync. To
              set expectations, selecting a quarter with 500 batches of GL transactions will
              take about 10 seconds to process.
            </InfoBox>
          </>
        </>
      )}

      {noRevisedTransactions && !noNewTransactions && (
        <>
          <br></br>
          <InfoBox variant="info" title={noRevisedTransactionsTitle} />
        </>
      )}
      {noRevisedTransactions && noNewTransactions && (
        <>
          <br></br>
          <InfoBox variant="info" title={noTransactionsTitle} />
        </>
      )}
    </>
  );
};

export default CustomEndDateCard;
